import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { DashboardAPI } from '@/apis';
import { parseIndustryName } from '@/app/dashboards/utils/benchmarks.utils';
import {
  FILTER_ACTION_TYPES,
  trackFilterInteraction,
} from '@/components/dashingUI/constants/mixpanel';
import { INDUSTRY_FOLLOWER_BUCKETS } from '@/models/dashboards/benchmark-type.enum';
import { logger } from '@/utils/logger';

export interface FollowerBucket {
  id: number;
  name: string;
}

export interface HubspotIndustry {
  id: number;
  name: string;
  follower_buckets: FollowerBucket[];
}

export interface BenchmarkSelection {
  industryId: number;
  industry: string;
  followerBucketId: number;
  followerBucket: string;
  bucketKey: string;
}

type BucketKey = keyof typeof INDUSTRY_FOLLOWER_BUCKETS;

interface MenuItem {
  label: string;
  command: () => void;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

export interface HubspotIndustryOption {
  value: string;
  text: string;
  label: string;
  id: number;
  follower_bucket: string;
  follower_bucket_id: number;
}

export const useDashboardIndustriesStore = defineStore('dashboard-industries', () => {
  const hubspotIndustries = ref<HubspotIndustry[]>([]);

  const benchmarkSelection = ref<BenchmarkSelection | null>(null);

  const benchmarkMenuModel = computed<MenuGroup[]>(() => {
    if (!hubspotIndustries.value?.length) return [];

    return hubspotIndustries.value.map((industry) => ({
      label: industry.name,
      items: (industry.follower_buckets ?? []).map((bucket) => {
        const bucketConfig = INDUSTRY_FOLLOWER_BUCKETS[bucket.name as BucketKey];
        const bucketLabel = bucketConfig?.text ?? bucket.name;
        const bucketKey = bucket.name;
        return {
          label: bucketLabel,
          command: () => {
            benchmarkSelection.value = {
              industryId: industry.id,
              industry: industry.name,
              followerBucketId: bucket.id,
              followerBucket: bucketLabel,
              bucketKey,
            };
            trackFilterInteraction({
              action: FILTER_ACTION_TYPES.APPLY,
              filterName: 'IndustryBenchmark',
              selectedOption: { industry: industry.name },
            });
          },
        };
      }),
    }));
  });

  const benchmarkFilterLabel = computed(() => {
    if (!benchmarkSelection.value) return 'Choose Industry';
    return `${benchmarkSelection.value.industry}: ${benchmarkSelection.value.followerBucket}`;
  });

  const selectedBenchmarkIndustry = computed<HubspotIndustry | null>(() => {
    if (!benchmarkSelection.value) return null;
    return (
      hubspotIndustries.value.find((ind) => ind.id === benchmarkSelection.value!.industryId) ?? null
    );
  });

  const selectedBenchmarkBucket = computed<FollowerBucket | null>(
    () =>
      selectedBenchmarkIndustry.value?.follower_buckets?.find(
        (b) => b.id === benchmarkSelection.value?.followerBucketId,
      ) ?? null,
  );

  function clearBenchmarkSelection() {
    if (benchmarkSelection.value) {
      trackFilterInteraction({
        action: FILTER_ACTION_TYPES.REMOVE,
        filterName: 'IndustryBenchmark',
        selectedOption: {},
      });
    }
    benchmarkSelection.value = null;
  }

  // Flattens hubspot industries with follower buckets into dropdown options
  // Each option displays as "Industry: Bucket" (e.g., "Beauty: Overall", "Beauty: 10K - 200K")
  const hubspotIndustryOptions = computed<HubspotIndustryOption[]>(() => {
    const options: HubspotIndustryOption[] = [];

    hubspotIndustries.value.forEach((industry) => {
      (industry.follower_buckets ?? []).forEach((bucket) => {
        const bucketConfig = INDUSTRY_FOLLOWER_BUCKETS[bucket.name as BucketKey];
        const bucketLabel = bucketConfig?.text ?? bucket.name;
        const text = parseIndustryName(industry.name, bucketLabel, true);
        const value = parseIndustryName(String(industry.id), bucket.name, true);

        options.push({
          value,
          text,
          label: text,
          id: industry.id,
          follower_bucket: bucket.name,
          follower_bucket_id: bucket.id,
        });
      });
    });

    return options;
  });

  async function fetchHubspotIndustries() {
    try {
      const response = await DashboardAPI.getHubspotIndustries();
      hubspotIndustries.value = response?.data ?? [];
    } catch (error) {
      logger.error('Failed to fetch hubspot industries:', error);
    }
  }

  return {
    hubspotIndustries,
    benchmarkSelection,
    benchmarkMenuModel,
    benchmarkFilterLabel,
    selectedBenchmarkIndustry,
    selectedBenchmarkBucket,
    clearBenchmarkSelection,
    hubspotIndustryOptions,
    fetchHubspotIndustries,
  };
});
